import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { Fade } from 'react-awesome-reveal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import StarField from '../components/StarField';
import bgSrc from '../artifacts/jj-ying-8bghKxNU1j0-unsplash.jpg';
import Card from '../components/Card';

const serviceCards = [
    {
        kicker: '01',
        title: 'System Modernization',
        descr: 'Stabilize aging systems, replace brittle workflows, and carve a practical path from legacy constraints to durable platforms.'
    },
    {
        kicker: '02',
        title: 'Custom Software',
        descr: 'Design and build focused applications, APIs, and internal tools for teams with specialized operational needs.'
    },
    {
        kicker: '03',
        title: 'Data & Database Engineering',
        descr: 'Model, tune, integrate, and expose data so systems stay reliable, explainable, and useful under real-world load.'
    },
    {
        kicker: '04',
        title: 'Workflow Automation',
        descr: 'Turn manual handoffs and fragmented processes into measurable, repeatable software-supported workflows.'
    },
    {
        kicker: '05',
        title: 'Technical Strategy',
        descr: 'Translate unclear needs into architecture, delivery plans, prototypes, and implementation-ready technical direction.'
    },
    {
        kicker: '06',
        title: 'Operational UX',
        descr: 'Shape user experiences for complex systems where speed, clarity, auditability, and trust matter more than decoration.'
    },
    {
        kicker: '07',
        title: 'Cloud & DevOps Enablement',
        descr: 'Design deployment paths, CI/CD pipelines, infrastructure patterns, and operational practices that help teams ship and maintain systems with confidence.'
    },
    {
        kicker: '08',
        title: 'API & Integration Engineering',
        descr: 'Connect applications, data sources, and services through clean APIs, secure integrations, and practical interface contracts that reduce friction between systems.'
    },
    {
        kicker: '09',
        title: 'AI-Assisted Problem Solving',
        descr: 'Use AI as a practical engineering accelerator for research, analysis, prototyping, documentation, and decision support while keeping human judgment in control.'
    }
];

const proofPoints = [
    'Public + private sector experience',
    'Maryland + Pennsylvania based',
    'Woman Owned Small Business',
    'Specialized software engineering',
    'AI-assisted delivery practices'
];

const Services = () => {
    const isTinyWidth = useMediaQuery({ query: '(max-width: 435px)' });

    React.useEffect(() => { document.title = 'Gripfire, Inc. - Services' }, []);

    return (
        <Box sx={rootSx}>
            <StarField />
            <Box sx={heroSx}>
                <Typography sx={eyebrowSx}>Engineer the path forward</Typography>
                <Typography variant="h2" sx={titleSx}>
                    Engineering support from first signal to fielded system.
                </Typography>

                <Typography variant="h6" sx={descrSx}>
                    Gripfire partners with teams that need more than generic development capacity. We help clarify the mission,
                    design durable technical paths, and deliver software that fits the workflow it serves. We also use AI
                    thoughtfully to accelerate research, compare options, and support better technical decisions without
                    replacing experienced engineering judgment.
                </Typography>

                <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={proofSx}>
                    {proofPoints.map((point) => (
                        <Chip key={point} label={point} sx={proofChipSx} />
                    ))}
                </Stack>
            </Box>

            <Box sx={servicesSx}>
                <Box sx={{ ...gridSx, ...(isTinyWidth ? { gridTemplateColumns: '1fr' } : {}) }}>
                    {serviceCards.map((service, index) => (
                        <Fade key={service.title} direction="up" triggerOnce delay={(index % 3) * 150}>
                            <Card kicker={service.kicker} title={service.title} descr={service.descr} />
                        </Fade>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default Services;

const rootSx = {
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: {
        xs: 'calc(5.75rem + env(safe-area-inset-top)) 0 calc(16rem + env(safe-area-inset-bottom)) 0',
        sm: '8rem 0 8rem 0'
    },
    '@media (orientation: landscape) and (max-height: 500px)': {
        paddingBottom: '10rem'
    },
    boxSizing: 'border-box',
    position: 'relative',
    overflowX: 'clip',
    '&::before': {
        opacity: '.12',
        backgroundImage: 'url(' + bgSrc + ')',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        position: 'absolute',
        content: '""'
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 20% 15%, rgba(255, 103, 35, .18), transparent 30%), linear-gradient(180deg, rgba(8,8,8,.88), rgba(8,8,8,.96))'
    }
};

const heroSx = {
    position: 'relative',
    zIndex: 1,
    width: 'min(1120px, calc(100% - 1.5rem))',
    mx: 'auto'
};

const eyebrowSx = {
    mb: 2,
    color: '#ff9b66',
    fontSize: '.78rem',
    fontWeight: 800,
    lineHeight: 1.4,
    letterSpacing: { xs: '.1em', sm: '.18em' },
    textTransform: 'uppercase'
};

const titleSx = {
    pb: 2,
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWeight: 800,
    fontSize: { xs: '2.25rem', sm: '3.75rem' },
    letterSpacing: { xs: '-.03em', sm: '-.045em' },
    lineHeight: 1.04
};

const descrSx = {
    maxWidth: '830px',
    color: 'rgba(255,255,255,.72)',
    lineHeight: 1.7
};

const proofSx = {
    mt: 3
};

const proofChipSx = {
    color: '#fff',
    border: '1px solid rgba(255,255,255,.16)',
    background: 'rgba(255,255,255,.06)'
};

const servicesSx = {
    position: 'relative',
    zIndex: 1,
    width: 'min(1120px, calc(100% - 1.5rem))',
    mx: 'auto',
    mt: '5rem',
};

const gridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
    gap: '1.25rem',
    mb: '70px',
    '@media (orientation: landscape) and (max-height: 500px)': {
        mb: '100px'
    }
};
